import React from 'react';
import Link from 'next/link';
import ProductCard from '../../components/CartFillerProductCard';
import styles from './CartFiller.module.css';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Card, CardActions, CardContent, CardMedia, Skeleton } from '@mui/material';
import Render from '../Render';

interface Props {
  addProductToCartMeta: { loading: boolean; success: boolean; failure: boolean; reset: () => void; };
  addProductToCart: (product_id: number, qty: string) => void;
  data: any[];
  meta: {
    failure: boolean;
    loading: boolean;
    message: string;
    reset: () => void;
    success: boolean;
  };
  loading: boolean;
  screenWidth: number;
  hideCartLink?: boolean
}

function CartFiller(props: Props) {
  /**
   * This component is currently written to display a maximum of 5 parts at a time. 
   * That can be changed by changing the default state values here and the values we increment/decrement by in the showNextGroupOfCartItems function
   **/
  const [numberOfProducts, setNumberOfProducts] = React.useState(5);
  const [cartFillerViewRange, setCartFillerViewRange] = React.useState([0, 0 + numberOfProducts]);
  const [productsInCart, setProductsInCart] = React.useState([]);
  const [filteredCart, setFilteredCart] = React.useState([]);
  
  React.useEffect(() => {
    const cartProducts = decodeURIComponent(document.cookie.replace(/(?:(?:^|.*;\s*)cart\s*\=\s*([^;]*).*$)|^.*$/, "$1")).split(',');

    setProductsInCart(cartProducts.map(product => {
      return product.split('|')[0];
    }));
  }, [props.addProductToCartMeta.success]);
  
  React.useEffect(() => {
    if (productsInCart.length !== 0 && props.data.length !== 0) {
      setFilteredCart(props.data.filter(part => {
        if (!productsInCart.includes(part.product_id.toString())) {
          return part;
        }
      }));
    }
  }, [productsInCart, props.data]);
  
  React.useEffect(() => {
    if (props.screenWidth >= 1200) {
      setNumberOfProducts(5);
    } else if (props.screenWidth >= 535) {
      setNumberOfProducts(3);
    } else {
      setNumberOfProducts(2);
    }
  },[props.screenWidth]);

  React.useEffect(() => {
    setCartFillerViewRange([0, 0 + numberOfProducts - 1]); // Minus one to account for array indexing starting at 0
  }, [numberOfProducts]);

  function showNextGroupOfCartItems(direction: string) {
    if (direction == 'up' && cartFillerViewRange[1] !== filteredCart.length - 1) {
      setCartFillerViewRange([cartFillerViewRange[0] + numberOfProducts, cartFillerViewRange[1] + numberOfProducts]);
    } else if (direction == 'down' && cartFillerViewRange[0] !== 0) {
      setCartFillerViewRange([cartFillerViewRange[0] - numberOfProducts, cartFillerViewRange[1] - numberOfProducts]);
    }

    if (direction == 'up' && cartFillerViewRange[1] >= filteredCart.length - 1) { // At the last "page" trying to go forward. Go back to begining
      setCartFillerViewRange([0, 0 + numberOfProducts - 1]);
    } else if (direction == 'down' && cartFillerViewRange[0] <= 0) { // At the first "page" trying to go back. Go to the last "page"
      setCartFillerViewRange([filteredCart.length - 5, filteredCart.length - 5 + numberOfProducts - 1]);
    }
  }
  
  return (
    <div style={{ width: '100%' }}>
      <div className={styles.title_container}>
        <h2 className={styles.title}>Product Recommendations</h2>
        {!props.hideCartLink && <Link href='cart'><a className={styles.cart_link}>Go to Cart &gt;</a></Link>}
      </div>
      <Render if={props.loading || (props.meta.success && filteredCart.length !== 0)}>
        <div className={styles.cart_history}>
          <div className={styles.arrow_container_left}>
            <Render if={filteredCart.length > 5}>
              <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => showNextGroupOfCartItems('down')} />
            </Render>
          </div>
          <div className={styles.products_container}>
            <Render if={props.meta.success}>
              {filteredCart.map((part, index) => {
                if (index >= cartFillerViewRange[0] && index <= cartFillerViewRange[1]) {
                  return (
                    <ProductCard
                      key={part.product_id}
                      data={part}
                      addProductToCart={props.addProductToCart}  
                      addProductToCartMeta={props.addProductToCartMeta}
                    />
                  );
                }
              })}  
            </Render>
            <Render if={props.loading}>
              <ProductListSkeleton numberOfProducts={numberOfProducts} />
            </Render>
          </div>
          <div className={styles.arrow_container_right}>
            <Render if={filteredCart.length > 5}>
              <ArrowForward sx={{ cursor: 'pointer' }} onClick={() => showNextGroupOfCartItems('up')} />
            </Render>
          </div>
        </div>
      </Render>
      <Render if={props.meta.success && filteredCart.length == 0}>
        <div className={styles.cart_history_no_products}>
          <p style={{ marginLeft: 10 }}>Parts removed from your cart without checking out with be shown here</p>
        </div>
      </Render>
    </div>
  );
}

export default CartFiller;

function ProductListSkeleton(props) {
  const [array, setArray] = React.useState([]);

  React.useEffect(() => {
    buildArray();
  }, [props.numberOfProducts]);

  function buildArray() {
    let counter = 1;
    let newArray = [];

    while (counter <= props.numberOfProducts) {
      newArray.push(counter);

      counter++;
    }

    setArray(newArray);
  }

  return (
    <>
      {array.map(i => {
        return (
          <Card key={i} className={styles.card_container}>
            <CardMedia
              component='img'
              height='120'
              sx={{ backgroundColor: '#ddd' }}
            />
            <CardContent>
              <Skeleton width={100} />
              <Skeleton width={50} />
            </CardContent>
            <CardActions className={styles.card_action_area}>
              <Skeleton height={25} width={35} variant='rectangular' />
              <Skeleton height={25} width={90} variant='rectangular' />
            </CardActions>
          </Card>
        );
      })}
    </>
  );
}